// RFM — segmentasi pelanggan Recency/Frequency/Monetary. Logika MURNI (diuji), tanpa DOM/Supabase.
// R = hari sejak servis terakhir (kecil = baru = bagus). F = jumlah transaksi. M = total belanja (LTV).
// Skor tiap dimensi 1..5 relatif populasi (kuintil). 10 segmen bernama (acuan tabel CRM Apo) dari
// kombinasi R×F×M; skor ditampilkan 3-digit "RFM" (mis. 555 = pelanggan terbaik).

export interface RfmInput { id: number; recencyDays: number; freq: number; monetary: number }
export interface RfmScored extends RfmInput { r: number; f: number; m: number; segmen: string }

/**
 * Skor kuintil 1..5 dari posisi `nilai` dalam populasi `urutAsc` (harus terurut menaik).
 * `terbalik`=true untuk Recency (hari kecil → skor besar). Memakai persentil "≤" sehingga banyak
 * nilai kembar tak membuat crash (semua kembar dapat skor sama). Catatan: populasi seragam →
 * semua dapat skor ekstrem yang sama (F/M=5, R=1), bukan kisaran tengah — wajar karena tak ada variasi.
 */
export function skorKuintil(nilai: number, urutAsc: number[], terbalik = false): number {
  const n = urutAsc.length;
  if (!n) return 3;
  let le = 0;
  for (const v of urutAsc) if (v <= nilai) le++;
  let s = Math.ceil((le / n) * 5);
  if (s < 1) s = 1; if (s > 5) s = 5;
  return terbalik ? 6 - s : s;
}

/** Skor RFM 3-digit untuk tampilan, mis. r5 f5 m5 → "555". */
export const skorRFM = (s: { r: number; f: number; m: number }): string => `${s.r}${s.f}${s.m}`;

/**
 * Segmen bernama dari skor R,F,M (acuan tabel CRM Apo — 10 kategori). Aturan berurutan dari yang
 * paling spesifik ke umum; SELALU mengembalikan tepat satu segmen untuk r,f,m ∈ 1..5 (tak ada
 * kombinasi tanpa segmen). Ini heuristik bisnis, bukan partisi kaku dari rentang skor di tabel
 * (rentang di tabel saling tumpang-tindih & hanya ilustrasi) — yang penting: setiap pelanggan
 * jatuh ke satu kategori yang masuk akal.
 */
export function segmenRFM(r: number, f: number, m = 3): string {
  if (r >= 4 && f >= 4 && m >= 4) return 'Pelanggan Juara';    // baru + sangat sering + belanja besar
  if (r <= 2 && f >= 4) return 'Jangan Kehilangan';           // dulu sangat sering, kini menjauh — rawan pindah (prioritas rescue)
  if (f >= 4 && m >= 4) return 'Loyalis Potensial';           // sering & besar, tapi tak sebaru Juara
  if (r >= 4 && f <= 2) return 'Pengguna Baru';               // baru mencoba, belum sering
  if (r >= 3 && f >= 3) return 'Pelanggan Setia';             // aktif & rutin, puas
  if (f >= 3 && m <= 2) return 'Sensitif Harga';              // sering tapi nilai kecil — tergantung promo
  if (r >= 3) return 'Butuh Perhatian';                       // agak baru tapi mulai jarang
  if (r === 2) return 'Hampir Tertidur';                      // lama tak datang, sebelumnya aktif
  if (r === 1 && f === 1) return 'Pelanggan Hilang';          // sudah tidak belanja sama sekali
  return 'Tertidur';                                          // lama tak belanja, masih bisa diaktifkan
}

export interface SegmenInfo { emoji: string; warna: string; penjelasan: string; tindakan: string[] }

/** Warna, penjelasan & tindakan CRM per segmen (acuan tabel Apo). */
export const SEGMEN_INFO: Record<string, SegmenInfo> = {
  'Pelanggan Juara': {
    emoji: '🔥', warna: 'var(--ok)',
    penjelasan: 'Baru beli, sangat sering, dan belanja besar. Kontributor utama pendapatan.',
    tindakan: ['Kirim personal thank-you note', 'Loyalty program eksklusif & early access produk baru', 'Premium service + survei kepuasan berkala', 'Berikan kado ulang tahun / rayakan hari besarnya (hampers)'],
  },
  'Loyalis Potensial': {
    emoji: '🌟', warna: 'var(--ok)',
    penjelasan: 'Sering beli, belanja besar, tapi tidak terlalu baru. Perlu dimotivasi agar semakin loyal.',
    tindakan: ['Tawarkan program membership', 'Upselling / cross-selling produk sejenis', 'Beri nilai tambah pelayanan atau bonus pembelian berulang'],
  },
  'Pelanggan Setia': {
    emoji: '🤝', warna: 'var(--ok)',
    penjelasan: 'Sering beli dan puas. Tidak perlu banyak insentif, tapi harus dijaga.',
    tindakan: ['Buat program referral', 'Beri badge VIP', 'Kirim ucapan personal (ultah, hari besar)', 'Minta testimoni untuk sosial media'],
  },
  'Pengguna Baru': {
    emoji: '🚀', warna: 'var(--blue)',
    penjelasan: 'Baru mencoba produk, belum menunjukkan loyalitas.',
    tindakan: ['Edukasi onboarding via WA/email', 'Panduan penggunaan produk', 'Penawaran ulang (repeat discount) dalam 7 hari'],
  },
  'Jangan Kehilangan': {
    emoji: '⚠️', warna: 'var(--danger)',
    penjelasan: 'Dulu belanja besar dan sering, tapi mulai menjauh. Rawan pindah ke kompetitor.',
    tindakan: ['Hubungi via WA/telepon langsung', 'Tawarkan benefit khusus jika kembali belanja', 'Beri rasa eksklusif agar merasa dihargai'],
  },
  'Butuh Perhatian': {
    emoji: '🧐', warna: 'var(--warn)',
    penjelasan: 'Dulu aktif tapi sekarang mulai menurun.',
    tindakan: ['Kirim kuis/feedback untuk tahu masalahnya', 'Personalisasi promosi', 'Follow-up dengan reminder produk favorit atau bundling menarik'],
  },
  'Sensitif Harga': {
    emoji: '💸', warna: 'var(--warn)',
    penjelasan: 'Pembelian tergantung diskon. Bukan loyal, tapi bisa diaktifkan dengan promo.',
    tindakan: ['Segmentasikan untuk promo flash sale', 'Kirim penawaran diskon terbatas', 'Optimalkan penjualan produk value-for-money'],
  },
  'Hampir Tertidur': {
    emoji: '😴', warna: 'var(--warn)',
    penjelasan: 'Lama tidak beli, sebelumnya aktif. Tanda-tanda menjauh.',
    tindakan: ['Kirim notifikasi "kami merindukanmu"', 'Beri diskon comeback', 'Ajak feedback alasan tidak belanja'],
  },
  'Tertidur': {
    emoji: '💤', warna: 'var(--muted)',
    penjelasan: 'Sudah lama tidak berbelanja, tapi masih bisa diaktifkan.',
    tindakan: ['Kampanye reaktivasi bertahap (WA/email/telepon)', 'Sertakan penawaran besar', 'Gali apakah ada masalah dengan produk/layanan'],
  },
  'Pelanggan Hilang': {
    emoji: '🧊', warna: 'var(--muted)',
    penjelasan: 'Sudah tidak belanja sama sekali. Kemungkinan pindah ke brand lain.',
    tindakan: ['Analisis penyebab churn', 'Jangan kirim penawaran biasa — gunakan brand repositioning', 'Undang survei untuk tahu alasan hilang, atau stop contact jika tak responsif'],
  },
};

/** Hitung skor & segmen untuk seluruh populasi (kuintil dihitung dari populasi ini). */
export function hitungRFM(inputs: RfmInput[]): RfmScored[] {
  const rArr = inputs.map((x) => x.recencyDays).sort((a, b) => a - b);
  const fArr = inputs.map((x) => x.freq).sort((a, b) => a - b);
  const mArr = inputs.map((x) => x.monetary).sort((a, b) => a - b);
  return inputs.map((x) => {
    const r = skorKuintil(x.recencyDays, rArr, true);
    const f = skorKuintil(x.freq, fArr, false);
    const m = skorKuintil(x.monetary, mArr, false);
    return { ...x, r, f, m, segmen: segmenRFM(r, f, m) };
  });
}
